import React, { useEffect, useState } from "react";
import api from "../../api";
import { Pagination, Stack } from "@mui/material";
import { Loader } from "./loader";
import moment from "moment";

// Lucide Icons
import {
  Search,
  CreditCard,
  Database,
  User,
  FileText,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  Globe,
  Smartphone,
  Mail,
  Phone,
  MapPin,
  Cake,
  AlertCircle,
  ExternalLink,
  Copy,
} from "lucide-react";
import "./CheckStripeCustomerData.css";
import { useAlert } from "react-alert";

function CheckCustomerDataStripe({ adminType }) {
  const alert = useAlert();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState([]);
  const [noOfUser, setnoOfUser] = useState(0);
  const [loader, setLoader] = useState(false);
  const [searchMail, setSearchMail] = useState("");
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [BTSubscriptionData, setBTSubscriptionData] = useState([]);
  const [invoiceData, setInvoiceData] = useState([]);
  const [customerData, setCustomerData] = useState({});
  const [DbLogs, setDbLogs] = useState([]);
  const [userDetails, setUserDetails] = useState([]);

  const handleSearch = async () => {
    try {
      if (searchMail) {
        let payload = {
          email: searchMail,
        };
        setLoader(true);
        const res = await api.getStripeCustomerData(payload);
        console.log(res);
        if (res?.success) {
          setSubscriptionData(res?.subscriptions || []);
          setInvoiceData(res?.invoices || []);
          setCustomerData(res?.customer || {});
          setDbLogs(res?.dbLogs || []);
          setUserDetails(res?.dbUser || []);
          setLoader(false);
        } else {
          alert.error("Email does not exist");
          setLoader(false);
        }
      } else {
        alert.error("Please insert email for search");
      }
    } catch (error) {
      alert.error("Invalid Email Address");
      setLoader(false);
    }
  };

  const formatDateFromTs = (date) => {
    const stampToDate = new Date(date * 1000);
    let dateTemp = moment(stampToDate).format("MMMM Do YYYY, h:mm:ss a");
    return dateTemp;
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    alert.info("Copied to clipboard!");
  };

  return (
    <>
      <div className="stripe-data-container">
        <div className="stripe-data-content">
          {/* Header Section */}
          <div className="stripe-header-section">
            <div className="stripe-header-main">
              <div className="stripe-title-section">
                <h1 className="stripe-page-title">
                  <CreditCard className="stripe-title-icon" />
                  Stripe Customer Analytics
                </h1>
                <p className="stripe-page-subtitle">
                  Track and analyze customer payment data from Stripe
                </p>
              </div>

              <div className="stripe-search-section">
                <div className="stripe-search-wrapper">
                  <Search className="stripe-search-icon" size={18} />
                  <input
                    type="email"
                    className="stripe-search-input"
                    placeholder="Search by customer email..."
                    value={searchMail}
                    onChange={(e) => setSearchMail(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <button
                    className="stripe-search-button"
                    onClick={handleSearch}
                  >
                    <Search size={16} />
                    Search
                  </button>
                </div>
                {/* <div className="stripe-search-hint">
                  <AlertCircle size={14} />
                  <span>Enter customer email to fetch Stripe & database records</span>
                </div> */}
              </div>
            </div>

            {/* Customer Info Card */}
            {customerData?.email && (
              <div className="stripe-customer-card">
                <div className="stripe-customer-header">
                  <div className="stripe-customer-avatar">
                    <User size={24} />
                  </div>
                  <div className="stripe-customer-info">
                    <h3 className="stripe-customer-name">
                      {customerData?.email}
                    </h3>
                    <div className="stripe-customer-meta">
                      <span className="stripe-customer-id">
                        <Shield size={14} />
                        {customerData?.id}
                      </span>
                      {subscriptionData.length > 0 && (
                        <span className="stripe-customer-next-payment">
                          <Calendar size={14} />
                          Next payment:{" "}
                          {formatDateFromTs(
                            subscriptionData[0]?.current_period_end,
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className="stripe-copy-button"
                    onClick={() => handleCopy(customerData?.id)}
                    title="Copy Customer ID"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Data Sections */}
          <div className="stripe-data-sections">
            {/* Stripe Data Section */}
            <div className="stripe-data-section">
              <div className="stripe-section-header">
                <CreditCard className="stripe-section-icon" />
                <h2 className="stripe-section-title">Stripe Data</h2>
              </div>

              <div className="stripe-tables-grid">
                {/* Subscriptions Table */}
                <div className="stripe-table-card">
                  <div className="stripe-table-header">
                    <FileText className="stripe-table-icon" />
                    <h3 className="stripe-table-title">Subscriptions</h3>
                    <span className="stripe-table-count">
                      {subscriptionData.length}
                    </span>
                  </div>
                  <StripeSubscriptionsTable
                    subscriptionData={subscriptionData}
                  />
                </div>

                {/* Invoices Table */}
                <div className="stripe-table-card">
                  <div className="stripe-table-header">
                    <DollarSign className="stripe-table-icon" />
                    <h3 className="stripe-table-title">Invoices</h3>
                    <span className="stripe-table-count">
                      {invoiceData.length}
                    </span>
                  </div>
                  <StripeInvoicesTable invoiceData={invoiceData} />
                </div>
              </div>
            </div>

            {/* Database Data Section */}
            <div className="stripe-data-section">
              <div className="stripe-section-header">
                <Database className="stripe-section-icon" />
                <h2 className="stripe-section-title">Database Records</h2>
              </div>

              <div className="stripe-tables-grid">
                {/* Subscription Logs */}
                <div className="stripe-table-card">
                  <div className="stripe-table-header">
                    <Clock className="stripe-table-icon" />
                    <h3 className="stripe-table-title">Subscription Logs</h3>
                    <span className="stripe-table-count">{DbLogs.length}</span>
                  </div>
                  <DatabaseLogsTable dbLogs={DbLogs} />
                </div>

                {/* User Details */}
                <div className="stripe-table-card">
                  <div className="stripe-table-header">
                    <User className="stripe-table-icon" />
                    <h3 className="stripe-table-title">User Details</h3>
                    <span className="stripe-table-count">
                      {userDetails.length}
                    </span>
                  </div>
                  <UserDetailsTable data={userDetails} />
                </div>
              </div>
            </div>

            {/* Braintree Section - Uncomment if needed */}
            {/* <div className="stripe-data-section">
              <div className="stripe-section-header">
                <Smartphone className="stripe-section-icon" />
                <h2 className="stripe-section-title">Braintree Data (Google Pay)</h2>
              </div>
              <BraintreeTable subscriptionData={BTSubscriptionData} />
            </div> */}
          </div>
        </div>
      </div>
      {loader && <Loader />}
    </>
  );
}

// Stripe Subscriptions Table Component
const StripeSubscriptionsTable = ({ subscriptionData }) => {
  const formatDateFromTs = (date) => {
    if (!date) return "-";
    const stampToDate = new Date(date * 1000);
    let dateTemp = moment(stampToDate).format("MMM Do YYYY, h:mm a");
    return dateTemp;
  };

  return (
    <>
      <div className="stripe-table-wrapper">
        <table className="stripe-data-table">
          <thead>
            <tr>
              <th>Subscription ID</th>
              <th>Status</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Cancel At End</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {subscriptionData.map((item, key) => (
              <tr key={key} className="stripe-data-row">
                <td>
                  <div className="stripe-cell-content">
                    <code className="stripe-id">{item?.id}</code>
                    <button
                      className="stripe-copy-small"
                      onClick={() => navigator.clipboard.writeText(item?.id)}
                      title="Copy ID"
                    >
                      <Copy size={10} />
                    </button>
                  </div>
                </td>
                <td>
                  {item.status === "active" ? (
                    <span className="stripe-badge stripe-badge-success">
                      <CheckCircle size={12} />
                      Active
                    </span>
                  ) : (
                    <span className="stripe-badge stripe-badge-error">
                      <XCircle size={12} />
                      {item.status}
                    </span>
                  )}
                </td>
                <td>
                  <div className="stripe-date-cell">
                    <Calendar size={12} />
                    {formatDateFromTs(item?.current_period_start)}
                  </div>
                </td>
                <td>
                  <div className="stripe-date-cell">
                    <Calendar size={12} />
                    {formatDateFromTs(item?.current_period_end)}
                  </div>
                </td>
                <td>
                  {item?.cancel_at_period_end ? (
                    <span className="stripe-badge stripe-badge-warning">
                      YES
                    </span>
                  ) : (
                    <span className="stripe-text-muted">-</span>
                  )}
                </td>
                <td>
                  <div className="stripe-amount-cell">
                    <DollarSign size={12} />
                    {(item.price?.unit_amount || item.plan?.amount || 0) / 100}
                    <span className="stripe-currency">
                      {item.price?.currency || item.plan?.currency || "USD"}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {subscriptionData.length === 0 && (
          <div className="stripe-empty-state">
            <FileText size={32} />
            <h4>No Subscriptions Found</h4>
            <p>Search for a customer to view subscription data</p>
          </div>
        )}
      </div>
    </>
  );
};

// Stripe Invoices Table Component
const StripeInvoicesTable = ({ invoiceData }) => {
  const formatDateFromTs = (date) => {
    if (!date) return "-";
    const stampToDate = new Date(date * 1000);
    return moment(stampToDate).format("MMM Do YYYY");
  };

  return (
    <>
      <div className="stripe-table-wrapper">
        <table className="stripe-data-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Payment Date</th>
              <th>Card Last 4</th>
              <th>Invoice Link</th>
            </tr>
          </thead>
          <tbody>
            {invoiceData.map((item, key) => (
              <tr key={key} className="stripe-data-row">
                <td>
                  <div className="stripe-cell-content">
                    <span className="stripe-invoice-number">
                      {item.number || item.id.substring(0, 12)}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="stripe-amount-cell">
                    <DollarSign size={12} />
                    {item.amount_paid / 100}
                    <span className="stripe-currency">USD</span>
                  </div>
                </td>
                <td>
                  {item.status === "paid" ? (
                    <span className="stripe-badge stripe-badge-success">
                      <CheckCircle size={12} />
                      Paid
                    </span>
                  ) : (
                    <span className="stripe-badge stripe-badge-error">
                      <XCircle size={12} />
                      {item.status}
                    </span>
                  )}
                </td>
                <td>
                  <div className="stripe-date-cell">
                    <Calendar size={12} />
                    {formatDateFromTs(item?.created)}
                  </div>
                </td>
                <td>
                  {item?.charge?.payment_method_details?.card?.last4 ? (
                    <span className="stripe-card-number">
                      •••• {item.charge.payment_method_details.card.last4}
                    </span>
                  ) : (
                    <span className="stripe-text-muted">-</span>
                  )}
                </td>
                <td>
                  {item?.hosted_invoice_url ? (
                    <a
                      href={item.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="stripe-invoice-link"
                    >
                      <ExternalLink size={12} />
                      View
                    </a>
                  ) : (
                    <span className="stripe-text-muted">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {invoiceData.length === 0 && (
          <div className="stripe-empty-state">
            <DollarSign size={32} />
            <h4>No Invoices Found</h4>
            <p>Search for a customer to view invoice data</p>
          </div>
        )}
      </div>
    </>
  );
};

// Database Logs Table Component
const DatabaseLogsTable = ({ dbLogs }) => {
  const formatDate = (date) => {
    if (!date) return "-";
    return moment(date).format("MMM Do YYYY, h:mm a");
  };

  return (
    <>
      <div className="stripe-table-wrapper">
        <table className="stripe-data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Subscription ID</th>
              <th>Method</th>
              <th>Status</th>
              <th>Amount</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {dbLogs.map((item, key) => (
              <tr key={key} className="stripe-data-row">
                <td>
                  <div className="stripe-cell-content">
                    <Mail size={12} />
                    {item?.email || "-"}
                  </div>
                </td>
                <td>
                  <code className="stripe-id">
                    {item?.subscriptionId || "-"}
                  </code>
                </td>
                <td>
                  <span className="stripe-text-muted">
                    {item?.subscribeVia || "-"}
                  </span>
                </td>
                <td>
                  {item.status === "success" ? (
                    <span className="stripe-badge stripe-badge-success">
                      <CheckCircle size={12} />
                      Success
                    </span>
                  ) : (
                    <span className="stripe-badge stripe-badge-error">
                      <XCircle size={12} />
                      {item?.status}
                    </span>
                  )}
                </td>
                <td>
                  <div className="stripe-amount-cell">
                    <DollarSign size={12} />
                    {item?.amount || "0"}
                  </div>
                </td>
                <td>
                  <div className="stripe-date-cell">
                    <Calendar size={12} />
                    {formatDate(item?.createdAt)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {dbLogs.length === 0 && (
          <div className="stripe-empty-state">
            <Clock size={32} />
            <h4>No Logs Found</h4>
            <p>Search for a customer to view database logs</p>
          </div>
        )}
      </div>
    </>
  );
};

// User Details Table Component
const UserDetailsTable = ({ data }) => {
  const formatDate = (date) => {
    if (!date) return "-";
    return moment(date).format("MMM Do YYYY");
  };

  const checkSubscription = (nextPayment) => {
    if (!nextPayment) return false;
    let currentDate = new Date();
    let endDate = new Date(nextPayment);
    return currentDate < endDate;
  };

  return (
    <>
      <div className="stripe-table-wrapper">
        <table className="stripe-data-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Contact</th>
              <th>Location</th>
              <th>Verified</th>
              <th>Subscription</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, key) => (
              <tr key={key} className="stripe-data-row">
                <td>
                  <div className="stripe-user-cell">
                    <div className="stripe-user-name">{item?.name || "-"}</div>
                    <div className="stripe-user-joined">
                      <Calendar size={10} />
                      Joined {formatDate(item?.createdAt)}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="stripe-contact-cell">
                    <div className="stripe-contact-item">
                      <Mail size={12} />
                      {item?.email || "-"}
                    </div>
                    {item?.phone && (
                      <div className="stripe-contact-item">
                        <Phone size={12} />
                        {item.phone}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <div className="stripe-location-cell">
                    {item?.country ? (
                      <>
                        <MapPin size={12} />
                        {item.country}
                      </>
                    ) : (
                      <span className="stripe-text-muted">-</span>
                    )}
                    {item?.dob && (
                      <div className="stripe-dob">
                        <Cake size={12} />
                        {item.dob}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  {item?.emailVerified ? (
                    <span className="stripe-badge stripe-badge-success">
                      <CheckCircle size={12} />
                      Verified
                    </span>
                  ) : (
                    <span className="stripe-badge stripe-badge-error">
                      <XCircle size={12} />
                      Not Verified
                    </span>
                  )}
                </td>
                <td>
                  {item?.subscription && item.subscription.length > 0 ? (
                    checkSubscription(item.subscription[0]?.nextPayment) ? (
                      <span className="stripe-badge stripe-badge-success">
                        Subscribed
                      </span>
                    ) : (
                      <span className="stripe-badge stripe-badge-error">
                        Expired
                      </span>
                    )
                  ) : (
                    <span className="stripe-text-muted">No Subscription</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="stripe-empty-state">
            <User size={32} />
            <h4>No User Data Found</h4>
            <p>Search for a customer to view user details</p>
          </div>
        )}
      </div>
    </>
  );
};

// Braintree Table Component (commented out in main component)
const BraintreeTable = ({ subscriptionData }) => {
  return (
    <div className="stripe-table-card">
      <div className="stripe-table-header">
        <Smartphone className="stripe-table-icon" />
        <h3 className="stripe-table-title">Braintree Subscriptions</h3>
        <span className="stripe-table-count">{subscriptionData.length}</span>
      </div>
      <div className="stripe-table-wrapper">
        <table className="stripe-data-table">
          <thead>
            <tr>
              <th>Subscription ID</th>
              <th>Status</th>
              <th>Start Date</th>
              <th>Next Billing</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {subscriptionData.map((item, key) => (
              <tr key={key} className="stripe-data-row">
                <td>
                  <code className="stripe-id">{item?.subscriptionId}</code>
                </td>
                <td>
                  {item.status === "active" || item.status === "Active" ? (
                    <span className="stripe-badge stripe-badge-success">
                      Active
                    </span>
                  ) : (
                    <span className="stripe-badge stripe-badge-error">
                      {item.status}
                    </span>
                  )}
                </td>
                <td>{item?.billingPeriodStartDate || "-"}</td>
                <td>{item?.nextBillingDate || "-"}</td>
                <td>{item.price || "0"} USD</td>
              </tr>
            ))}
          </tbody>
        </table>
        {subscriptionData.length === 0 && (
          <div className="stripe-empty-state">
            <Smartphone size={32} />
            <h4>No Braintree Data</h4>
            <p>No Braintree subscription data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckCustomerDataStripe;
